import React, { useEffect, useState } from 'react';
import { pulseSurveyAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Plus, Send } from 'lucide-react';

const QUESTION_TYPES = ['rating', 'nps', 'text', 'single_choice', 'multi_choice'];
const TARGET_SCOPES = ['all', 'department', 'location', 'designation', 'employee_ids', 'custom'];

const PulseSurveysModule = () => {
  const [loading, setLoading] = useState(false);
  const [surveys, setSurveys] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [surveyDetail, setSurveyDetail] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [responses, setResponses] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [actionPlans, setActionPlans] = useState([]);

  const [newSurvey, setNewSurvey] = useState({
    title: '',
    description: '',
    is_anonymous: true,
    target_scope: 'all',
    target_filters_json: '{}',
  });
  const [newQuestion, setNewQuestion] = useState({
    question_text: '',
    question_type: 'rating',
    is_required: true,
    sort_order: 1,
    options_json: '[]',
  });
  const [newActionPlan, setNewActionPlan] = useState({
    title: '',
    description: '',
    owner_employee_id: '',
    due_date: '',
  });

  const loadSurveys = async () => {
    const res = await pulseSurveyAPI.listSurveys();
    setSurveys(res.data || []);
    if (!selectedSurveyId && res.data?.length) {
      setSelectedSurveyId(res.data[0].id);
    }
  };

  const loadSurveyData = async (surveyId) => {
    if (!surveyId) return;
    setLoading(true);
    try {
      const [detailRes, recipientsRes, responsesRes, answersRes, analyticsRes, plansRes] = await Promise.all([
        pulseSurveyAPI.getSurveyDetail(surveyId),
        pulseSurveyAPI.listRecipients(surveyId),
        pulseSurveyAPI.listResponses(surveyId),
        pulseSurveyAPI.listAnswers(surveyId),
        pulseSurveyAPI.getAnalytics(surveyId),
        pulseSurveyAPI.listActionPlans(surveyId),
      ]);
      setSurveyDetail(detailRes.data);
      setRecipients(recipientsRes.data || []);
      setResponses(responsesRes.data || []);
      setAnswers(answersRes.data || []);
      setAnalytics(analyticsRes.data || null);
      setActionPlans(plansRes.data || []);
    } catch (error) {
      toast.error('Failed to load selected survey data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadSurveys();
      } catch (error) {
        toast.error('Failed to load surveys');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSurveyData(selectedSurveyId);
  }, [selectedSurveyId]);

  const handleCreateSurvey = async () => {
    if (!newSurvey.title.trim()) {
      toast.error('Survey title is required');
      return;
    }
    try {
      let targetFilters = {};
      try {
        targetFilters = JSON.parse(newSurvey.target_filters_json || '{}');
      } catch {
        toast.error('Target filters must be valid JSON');
        return;
      }
      const payload = {
        title: newSurvey.title,
        description: newSurvey.description,
        is_anonymous: newSurvey.is_anonymous,
        target_scope: newSurvey.target_scope,
        target_filters: targetFilters,
      };
      const res = await pulseSurveyAPI.createSurvey(payload);
      toast.success('Survey created');
      setNewSurvey({ title: '', description: '', is_anonymous: true, target_scope: 'all', target_filters_json: '{}' });
      await loadSurveys();
      if (res.data?.survey?.id) setSelectedSurveyId(res.data.survey.id);
    } catch (error) {
      toast.error('Failed to create survey');
    }
  };

  const handleAddQuestion = async () => {
    if (!selectedSurveyId) return;
    if (!newQuestion.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }
    try {
      let options = [];
      try {
        options = JSON.parse(newQuestion.options_json || '[]');
      } catch {
        toast.error('Question options must be valid JSON array');
        return;
      }
      await pulseSurveyAPI.addQuestion(selectedSurveyId, {
        question_text: newQuestion.question_text,
        question_type: newQuestion.question_type,
        is_required: newQuestion.is_required,
        sort_order: Number(newQuestion.sort_order) || 1,
        options_json: options,
      });
      toast.success('Question added');
      setNewQuestion({ question_text: '', question_type: 'rating', is_required: true, sort_order: 1, options_json: '[]' });
      await loadSurveyData(selectedSurveyId);
    } catch (error) {
      toast.error('Failed to add question');
    }
  };

  const handlePublishSurvey = async () => {
    if (!selectedSurveyId) return;
    try {
      const res = await pulseSurveyAPI.publishSurvey(selectedSurveyId);
      toast.success(`Survey published (${res.data?.recipients_added || 0} recipients added)`);
      await loadSurveyData(selectedSurveyId);
      await loadSurveys();
    } catch (error) {
      toast.error('Failed to publish survey');
    }
  };

  const handleCreateActionPlan = async () => {
    if (!selectedSurveyId) return;
    if (!newActionPlan.title.trim()) {
      toast.error('Action plan title is required');
      return;
    }
    try {
      await pulseSurveyAPI.createActionPlan(selectedSurveyId, {
        title: newActionPlan.title,
        description: newActionPlan.description,
        owner_employee_id: newActionPlan.owner_employee_id || null,
        due_date: newActionPlan.due_date || null,
      });
      toast.success('Action plan created');
      setNewActionPlan({ title: '', description: '', owner_employee_id: '', due_date: '' });
      await loadSurveyData(selectedSurveyId);
    } catch (error) {
      toast.error('Failed to create action plan');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Pulse Surveys</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Survey Title</Label>
              <Input value={newSurvey.title} onChange={(e) => setNewSurvey({ ...newSurvey, title: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Target Scope</Label>
              <Select value={newSurvey.target_scope} onValueChange={(v) => setNewSurvey({ ...newSurvey, target_scope: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TARGET_SCOPES.map((scope) => (
                    <SelectItem key={scope} value={scope}>{scope}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={newSurvey.description} onChange={(e) => setNewSurvey({ ...newSurvey, description: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Target Filters JSON</Label>
            <Textarea value={newSurvey.target_filters_json} onChange={(e) => setNewSurvey({ ...newSurvey, target_filters_json: e.target.value })} />
          </div>
          <div className="flex justify-end">
            <Button onClick={handleCreateSurvey}>
              <Plus className="h-4 w-4 mr-2" />
              Create Survey
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-1">
          <CardHeader>
            <CardTitle>Surveys</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[640px] overflow-y-auto">
            {surveys.map((survey) => (
              <button
                key={survey.id}
                type="button"
                onClick={() => setSelectedSurveyId(survey.id)}
                className={`w-full text-left p-3 rounded-lg border hover:bg-muted/40 ${selectedSurveyId === survey.id ? 'border-primary bg-primary/5' : ''}`}
              >
                <p className="font-medium">{survey.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline">{survey.status}</Badge>
                  <Badge variant="outline">{survey.target_scope}</Badge>
                </div>
              </button>
            ))}
            {surveys.length === 0 && <p className="text-sm text-muted-foreground">No surveys found.</p>}
          </CardContent>
        </Card>

        <div className="xl:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Survey Builder</CardTitle>
                <Button variant="outline" onClick={handlePublishSurvey} disabled={!selectedSurveyId || loading}>
                  <Send className="h-4 w-4 mr-2" />
                  Publish & Target
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Question Type</Label>
                  <Select value={newQuestion.question_type} onValueChange={(v) => setNewQuestion({ ...newQuestion, question_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {QUESTION_TYPES.map((qType) => (
                        <SelectItem key={qType} value={qType}>{qType}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={newQuestion.sort_order}
                    onChange={(e) => setNewQuestion({ ...newQuestion, sort_order: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Question Text</Label>
                <Input value={newQuestion.question_text} onChange={(e) => setNewQuestion({ ...newQuestion, question_text: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Options JSON (for single/multi choice)</Label>
                <Input value={newQuestion.options_json} onChange={(e) => setNewQuestion({ ...newQuestion, options_json: e.target.value })} />
              </div>
              <Button onClick={handleAddQuestion} disabled={!selectedSurveyId || loading}>Add Question</Button>
              <div className="space-y-2">
                <p className="text-sm font-medium">Questions</p>
                {(surveyDetail?.questions || []).map((question) => (
                  <div key={question.id} className="p-2 border rounded-md text-sm">
                    {question.sort_order}. {question.question_text}
                    <span className="text-muted-foreground ml-2">({question.question_type})</span>
                  </div>
                ))}
                {(surveyDetail?.questions || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">No questions added yet.</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Participation & Analytics</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-3 text-sm">
              <div className="p-3 border rounded-md">
                <p className="text-muted-foreground">Recipients</p>
                <p className="text-xl font-semibold">{analytics?.participation?.recipients || 0}</p>
              </div>
              <div className="p-3 border rounded-md">
                <p className="text-muted-foreground">Responses</p>
                <p className="text-xl font-semibold">{analytics?.participation?.responses || 0}</p>
              </div>
              <div className="p-3 border rounded-md">
                <p className="text-muted-foreground">Response Rate</p>
                <p className="text-xl font-semibold">{analytics?.participation?.response_rate || 0}%</p>
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle>Recipients</CardTitle></CardHeader>
              <CardContent className="space-y-2 max-h-[300px] overflow-y-auto">
                {recipients.map((recipient) => (
                  <div key={recipient.id} className="p-2 border rounded-md text-sm flex justify-between items-center">
                    <span>{recipient.employee_id}</span>
                    <Badge variant="outline">{recipient.invitation_status}</Badge>
                  </div>
                ))}
                {recipients.length === 0 && <p className="text-sm text-muted-foreground">No recipients.</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Responses</CardTitle></CardHeader>
              <CardContent className="space-y-2 max-h-[300px] overflow-y-auto">
                {responses.map((response) => (
                  <div key={response.id} className="p-2 border rounded-md text-sm">
                    <p>ID: {response.id}</p>
                    <p className="text-muted-foreground">Sentiment: {response.sentiment_label} ({response.sentiment_score})</p>
                  </div>
                ))}
                {responses.length === 0 && <p className="text-sm text-muted-foreground">No responses.</p>}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle>Answers</CardTitle></CardHeader>
            <CardContent className="space-y-2 max-h-[260px] overflow-y-auto">
              {answers.map((answer) => (
                <div key={answer.id} className="p-2 border rounded-md text-sm">
                  <p>Q#{answer.question_id} / Response#{answer.response_id}</p>
                  <p className="text-muted-foreground">{answer.answer_text || answer.answer_numeric || JSON.stringify(answer.answer_options || [])}</p>
                </div>
              ))}
              {answers.length === 0 && <p className="text-sm text-muted-foreground">No answers.</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Action Plans</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <Input
                  placeholder="Action title"
                  value={newActionPlan.title}
                  onChange={(e) => setNewActionPlan({ ...newActionPlan, title: e.target.value })}
                />
                <Input
                  placeholder="Owner employee id (optional)"
                  value={newActionPlan.owner_employee_id}
                  onChange={(e) => setNewActionPlan({ ...newActionPlan, owner_employee_id: e.target.value })}
                />
              </div>
              <Textarea
                placeholder="Description"
                value={newActionPlan.description}
                onChange={(e) => setNewActionPlan({ ...newActionPlan, description: e.target.value })}
              />
              <Input
                type="date"
                value={newActionPlan.due_date}
                onChange={(e) => setNewActionPlan({ ...newActionPlan, due_date: e.target.value })}
              />
              <Button onClick={handleCreateActionPlan} disabled={!selectedSurveyId || loading}>Create Action Plan</Button>
              <div className="space-y-2">
                {actionPlans.map((plan) => (
                  <div key={plan.id} className="p-2 border rounded-md text-sm flex justify-between items-center">
                    <div>
                      <p className="font-medium">{plan.title}</p>
                      <p className="text-muted-foreground">{plan.owner_employee_id || 'Unassigned'} • due {plan.due_date || '-'}</p>
                    </div>
                    <Badge variant="outline">{plan.status}</Badge>
                  </div>
                ))}
                {actionPlans.length === 0 && <p className="text-sm text-muted-foreground">No action plans.</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PulseSurveysModule;

